import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import { ArrowLeft, ArrowRight, Phone, Globe, Router, Wrench } from "lucide-react";
import { motion } from "framer-motion";
import heroBg from "@/assets/vdsl-hero-bg.jpg";

const features = [
  {
    icon: Globe,
    title: "Breed beschikbaar",
    text: "VDSL werkt via de telefoonlijn en is daardoor op veel plekken beschikbaar — ook waar glasvezel nog niet ligt.",
  },
  {
    icon: Router,
    title: "Onze eigen router",
    text: "We plaatsen altijd onze eigen router zodat wij maximale controle hebben en jij minimale storingen hebt.",
  },
  {
    icon: Phone,
    title: "Persoonlijke support",
    text: "Haperingen? Bel ons en je hebt direct iemand aan de lijn die kan meekijken en oplossen.",
  },
  {
    icon: Wrench,
    title: "Inclusief netwerkbeheer",
    text: "Bij Rinqel zit netwerkbeheer altijd inbegrepen. Je betaalt een vaste prijs, zonder verrassingen.",
  },
];

const VDSLPage = () => {
  return (
    <>
      {/* Hero — full-width met achtergrondafbeelding */}
      <section className="relative min-h-[480px] overflow-hidden">
        <img
          src={heroBg}
          alt="Netwerkinfrastructuur en serverruimte"
          loading="eager" decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/25" />

        <div className="container relative z-10 flex min-h-[480px] items-center py-20">
          <div className="max-w-2xl">
            <Link
              to="/diensten/internet"
              className="mb-6 inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Terug naar internet
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Inclusief netwerkbeheer · Geen verborgen kosten
              </span>
            </motion.div>

            <motion.h1
              className="mt-4 text-4xl font-bold leading-[1.1] tracking-tight text-white md:text-6xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Rinqel <span className="text-gradient">VDSL</span>
            </motion.h1>

            <motion.p
              className="mt-5 max-w-xl text-lg leading-relaxed text-white/80"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Betrouwbaar zakelijk internet via de telefoonlijn. Voor locaties waar glasvezel (nog) niet beschikbaar is.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Tarieven */}
      <section className="py-16 md:py-20">
        <div className="container">
          <div className="mx-auto max-w-5xl text-center">
            <h2 className="text-2xl font-bold md:text-3xl">Tarieven</h2>
            <p className="mt-2 text-muted-foreground">Alles inbegrepen, geen verborgen kosten</p>
          </div>

          <div className="mx-auto mt-10 grid max-w-5xl gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                name: "KPN Zakelijk VDSL",
                speed: "tot 50 Mbit/s",
                price: "55",
                features: ["Vast IP-adres", "Rinqel managed router", "Netwerkbeheer", "Persoonlijke support"],
              },
              {
                name: "KPN Zakelijk VDSL",
                speed: "tot 100 Mbit/s",
                price: "60",
                features: ["Vast IP-adres", "Rinqel managed router", "Netwerkbeheer", "Persoonlijke support"],
              },
              {
                name: "KPN Zakelijk BVDSL",
                speed: "tot 100 Mbit/s",
                price: "60",
                features: ["Vast IP-adres", "Rinqel managed router", "Netwerkbeheer", "Persoonlijke support"],
              },
              {
                name: "KPN Zakelijk BVDSL",
                speed: "tot 200 Mbit/s",
                price: "65",
                features: ["Vast IP-adres", "Rinqel managed router", "Netwerkbeheer", "Persoonlijke support"],
              },
            ].map((plan, i) => (
              <motion.div
                key={`${plan.name}-${plan.speed}`}
                className="flex flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition-shadow hover:shadow-card-hover"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <h3 className="text-lg font-bold">{plan.name}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{plan.speed}</p>

                <ul className="mt-5 flex-1 space-y-2">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="mt-0.5 text-rinqel-green">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>

                <div className="mt-6 border-t border-border pt-4">
                  <span className="text-sm text-muted-foreground">vanaf</span>
                  <span className="ml-1 text-3xl font-bold text-gradient">€{plan.price}</span>
                  <span className="text-sm text-muted-foreground"> /per maand</span>
                </div>

                <Button variant="hero" size="sm" className="mt-4 w-full" asChild>
                  <TrackedLink to="/contact" trackLabel="Vraag aan" trackLocation="vdsl-plan-card">Vraag aan</TrackedLink>
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Over VDSL */}
      <section className="bg-muted/40 py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Over VDSL{" "}
              <span className="font-normal text-muted-foreground">(en waarom het langzaam verdwijnt)</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              VDSL is een beetje de "huistelefoon" van internetverbindingen. Het werkt nog steeds prima, maar
              het is duidelijk een uitlopend product. Het wordt actief vervangen door glasvezel in steeds meer
              gebieden. We gebruiken het nog alleen op plekken waar (nog) geen stabiele glasvezelverbinding
              beschikbaar is.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-20">
        <div className="container">
          <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-2">
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  className="flex gap-4 rounded-xl border border-border bg-card p-6 shadow-card transition-shadow hover:shadow-card-hover"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                >
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-primary-foreground ${
                    i % 3 === 0 ? "bg-hero-gradient" : i % 3 === 1 ? "bg-rinqel-blue-gradient" : "bg-rinqel-green-gradient"
                  }`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{feature.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{feature.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-muted/40 py-12">
        <div className="container">
          <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:justify-center">
            <Button variant="hero" size="lg" asChild>
              <TrackedLink to="/configurator" trackLabel="Bereken je prijs" trackLocation="vdsl-cta">
                Bereken je prijs
                <ArrowRight className="ml-1 h-4 w-4" />
              </TrackedLink>
            </Button>
            <Button variant="hero-outline" size="lg" asChild>
              <TrackedLink to="/contact" trackLabel="Neem contact op" trackLocation="vdsl-cta">Neem contact op</TrackedLink>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default VDSLPage;
